/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const U = globalThis, J = U.ShadowRoot && (U.ShadyCSS === void 0 || U.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Q = Symbol(), re = /* @__PURE__ */ new WeakMap();
let be = class {
  constructor(e, t, r) {
    if (this._$cssResult$ = !0, r !== Q) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = t;
  }
  get styleSheet() {
    let e = this.o;
    const t = this.t;
    if (J && e === void 0) {
      const r = t !== void 0 && t.length === 1;
      r && (e = re.get(t)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), r && re.set(t, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const me = (n) => new be(typeof n == "string" ? n : n + "", void 0, Q), Ae = (n, ...e) => {
  const t = n.length === 1 ? n[0] : e.reduce((r, i, s) => r + ((o) => {
    if (o._$cssResult$ === !0) return o.cssText;
    if (typeof o == "number") return o;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + o + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + n[s + 1], n[0]);
  return new be(t, n, Q);
}, Ee = (n, e) => {
  if (J) n.adoptedStyleSheets = e.map((t) => t instanceof CSSStyleSheet ? t : t.styleSheet);
  else for (const t of e) {
    const r = document.createElement("style"), i = U.litNonce;
    i !== void 0 && r.setAttribute("nonce", i), r.textContent = t.cssText, n.appendChild(r);
  }
}, ie = J ? (n) => n : (n) => n instanceof CSSStyleSheet ? ((e) => {
  let t = "";
  for (const r of e.cssRules) t += r.cssText;
  return me(t);
})(n) : n;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Ce, defineProperty: Se, getOwnPropertyDescriptor: ze, getOwnPropertyNames: He, getOwnPropertySymbols: Me, getPrototypeOf: Pe } = Object, g = globalThis, ne = g.trustedTypes, Re = ne ? ne.emptyScript : "", G = g.reactiveElementPolyfillSupport, E = (n, e) => n, B = { toAttribute(n, e) {
  switch (e) {
    case Boolean:
      n = n ? Re : null;
      break;
    case Object:
    case Array:
      n = n == null ? n : JSON.stringify(n);
  }
  return n;
}, fromAttribute(n, e) {
  let t = n;
  switch (e) {
    case Boolean:
      t = n !== null;
      break;
    case Number:
      t = n === null ? null : Number(n);
      break;
    case Object:
    case Array:
      try {
        t = JSON.parse(n);
      } catch {
        t = null;
      }
  }
  return t;
} }, Z = (n, e) => !Ce(n, e), se = { attribute: !0, type: String, converter: B, reflect: !1, useDefault: !1, hasChanged: Z };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), g.litPropertyMetadata ?? (g.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let $ = class extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, t = se) {
    if (t.state && (t.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(e) && ((t = Object.create(t)).wrapped = !0), this.elementProperties.set(e, t), !t.noAccessor) {
      const r = Symbol(), i = this.getPropertyDescriptor(e, r, t);
      i !== void 0 && Se(this.prototype, e, i);
    }
  }
  static getPropertyDescriptor(e, t, r) {
    const { get: i, set: s } = ze(this.prototype, e) ?? { get() {
      return this[t];
    }, set(o) {
      this[t] = o;
    } };
    return { get: i, set(o) {
      const d = i == null ? void 0 : i.call(this);
      s == null || s.call(this, o), this.requestUpdate(e, d, r);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? se;
  }
  static _$Ei() {
    if (this.hasOwnProperty(E("elementProperties"))) return;
    const e = Pe(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(E("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(E("properties"))) {
      const t = this.properties, r = [...He(t), ...Me(t)];
      for (const i of r) this.createProperty(i, t[i]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const t = litPropertyMetadata.get(e);
      if (t !== void 0) for (const [r, i] of t) this.elementProperties.set(r, i);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [t, r] of this.elementProperties) {
      const i = this._$Eu(t, r);
      i !== void 0 && this._$Eh.set(i, t);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const r = new Set(e.flat(1 / 0).reverse());
      for (const i of r) t.unshift(ie(i));
    } else e !== void 0 && t.push(ie(e));
    return t;
  }
  static _$Eu(e, t) {
    const r = t.attribute;
    return r === !1 ? void 0 : typeof r == "string" ? r : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((t) => t(this));
  }
  addController(e) {
    var t;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((t = e.hostConnected) == null || t.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$EO) == null || t.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), t = this.constructor.elementProperties;
    for (const r of t.keys()) this.hasOwnProperty(r) && (e.set(r, this[r]), delete this[r]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return Ee(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((t) => {
      var r;
      return (r = t.hostConnected) == null ? void 0 : r.call(t);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((t) => {
      var r;
      return (r = t.hostDisconnected) == null ? void 0 : r.call(t);
    });
  }
  attributeChangedCallback(e, t, r) {
    this._$AK(e, r);
  }
  _$ET(e, t) {
    var s;
    const r = this.constructor.elementProperties.get(e), i = this.constructor._$Eu(e, r);
    if (i !== void 0 && r.reflect === !0) {
      const o = (((s = r.converter) == null ? void 0 : s.toAttribute) !== void 0 ? r.converter : B).toAttribute(t, r.type);
      this._$Em = e, o == null ? this.removeAttribute(i) : this.setAttribute(i, o), this._$Em = null;
    }
  }
  _$AK(e, t) {
    var s, o;
    const r = this.constructor, i = r._$Eh.get(e);
    if (i !== void 0 && this._$Em !== i) {
      const d = r.getPropertyOptions(i), a = typeof d.converter == "function" ? { fromAttribute: d.converter } : ((s = d.converter) == null ? void 0 : s.fromAttribute) !== void 0 ? d.converter : B;
      this._$Em = i, this[i] = a.fromAttribute(t, d.type) ?? ((o = this._$Ej) == null ? void 0 : o.get(i)) ?? null, this._$Em = null;
    }
  }
  requestUpdate(e, t, r) {
    var i;
    if (e !== void 0) {
      const s = this.constructor, o = this[e];
      if (r ?? (r = s.getPropertyOptions(e)), !((r.hasChanged ?? Z)(o, t) || r.useDefault && r.reflect && o === ((i = this._$Ej) == null ? void 0 : i.get(e)) && !this.hasAttribute(s._$Eu(e, r)))) return;
      this.C(e, t, r);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(e, t, { useDefault: r, reflect: i, wrapped: s }, o) {
    r && !(this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Map())).has(e) && (this._$Ej.set(e, o ?? t ?? this[e]), s !== !0 || o !== void 0) || (this._$AL.has(e) || (this.hasUpdated || r || (t = void 0), this._$AL.set(e, t)), i === !0 && this._$Em !== e && (this._$Eq ?? (this._$Eq = /* @__PURE__ */ new Set())).add(e));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var r;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [s, o] of this._$Ep) this[s] = o;
        this._$Ep = void 0;
      }
      const i = this.constructor.elementProperties;
      if (i.size > 0) for (const [s, o] of i) {
        const { wrapped: d } = o, a = this[s];
        d !== !0 || this._$AL.has(s) || a === void 0 || this.C(s, void 0, o, a);
      }
    }
    let e = !1;
    const t = this._$AL;
    try {
      e = this.shouldUpdate(t), e ? (this.willUpdate(t), (r = this._$EO) == null || r.forEach((i) => {
        var s;
        return (s = i.hostUpdate) == null ? void 0 : s.call(i);
      }), this.update(t)) : this._$EM();
    } catch (i) {
      throw e = !1, this._$EM(), i;
    }
    e && this._$AE(t);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$EO) == null || t.forEach((r) => {
      var i;
      return (i = r.hostUpdated) == null ? void 0 : i.call(r);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Eq && (this._$Eq = this._$Eq.forEach((t) => this._$ET(t, this[t]))), this._$EM();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
$.elementStyles = [], $.shadowRootOptions = { mode: "open" }, $[E("elementProperties")] = /* @__PURE__ */ new Map(), $[E("finalized")] = /* @__PURE__ */ new Map(), G == null || G({ ReactiveElement: $ }), (g.reactiveElementVersions ?? (g.reactiveElementVersions = [])).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const C = globalThis, N = C.trustedTypes, oe = N ? N.createPolicy("lit-html", { createHTML: (n) => n }) : void 0, ge = "$lit$", m = `lit$${Math.random().toFixed(9).slice(2)}$`, fe = "?" + m, Oe = `<${fe}>`, x = document, H = () => x.createComment(""), M = (n) => n === null || typeof n != "object" && typeof n != "function", ee = Array.isArray, Te = (n) => ee(n) || typeof (n == null ? void 0 : n[Symbol.iterator]) == "function", q = `[ 	
\f\r]`, A = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ae = /-->/g, de = />/g, f = RegExp(`>|${q}(?:([^\\s"'>=/]+)(${q}*=${q}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), le = /'/g, he = /"/g, ke = /^(?:script|style|textarea|title)$/i, Ue = (n) => (e, ...t) => ({ _$litType$: n, strings: e, values: t }), T = Ue(1), v = Symbol.for("lit-noChange"), h = Symbol.for("lit-nothing"), ce = /* @__PURE__ */ new WeakMap(), k = x.createTreeWalker(x, 129);
function we(n, e) {
  if (!ee(n) || !n.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return oe !== void 0 ? oe.createHTML(e) : e;
}
const Be = (n, e) => {
  const t = n.length - 1, r = [];
  let i, s = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", o = A;
  for (let d = 0; d < t; d++) {
    const a = n[d];
    let c, p, l = -1, u = 0;
    for (; u < a.length && (o.lastIndex = u, p = o.exec(a), p !== null); ) u = o.lastIndex, o === A ? p[1] === "!--" ? o = ae : p[1] !== void 0 ? o = de : p[2] !== void 0 ? (ke.test(p[2]) && (i = RegExp("</" + p[2], "g")), o = f) : p[3] !== void 0 && (o = f) : o === f ? p[0] === ">" ? (o = i ?? A, l = -1) : p[1] === void 0 ? l = -2 : (l = o.lastIndex - p[2].length, c = p[1], o = p[3] === void 0 ? f : p[3] === '"' ? he : le) : o === he || o === le ? o = f : o === ae || o === de ? o = A : (o = f, i = void 0);
    const b = o === f && n[d + 1].startsWith("/>") ? " " : "";
    s += o === A ? a + Oe : l >= 0 ? (r.push(c), a.slice(0, l) + ge + a.slice(l) + m + b) : a + m + (l === -2 ? d : b);
  }
  return [we(n, s + (n[t] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), r];
};
class P {
  constructor({ strings: e, _$litType$: t }, r) {
    let i;
    this.parts = [];
    let s = 0, o = 0;
    const d = e.length - 1, a = this.parts, [c, p] = Be(e, t);
    if (this.el = P.createElement(c, r), k.currentNode = this.el.content, t === 2 || t === 3) {
      const l = this.el.content.firstChild;
      l.replaceWith(...l.childNodes);
    }
    for (; (i = k.nextNode()) !== null && a.length < d; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) for (const l of i.getAttributeNames()) if (l.endsWith(ge)) {
          const u = p[o++], b = i.getAttribute(l).split(m), O = /([.?@])?(.*)/.exec(u);
          a.push({ type: 1, index: s, name: O[2], strings: b, ctor: O[1] === "." ? Le : O[1] === "?" ? De : O[1] === "@" ? je : D }), i.removeAttribute(l);
        } else l.startsWith(m) && (a.push({ type: 6, index: s }), i.removeAttribute(l));
        if (ke.test(i.tagName)) {
          const l = i.textContent.split(m), u = l.length - 1;
          if (u > 0) {
            i.textContent = N ? N.emptyScript : "";
            for (let b = 0; b < u; b++) i.append(l[b], H()), k.nextNode(), a.push({ type: 2, index: ++s });
            i.append(l[u], H());
          }
        }
      } else if (i.nodeType === 8) if (i.data === fe) a.push({ type: 2, index: s });
      else {
        let l = -1;
        for (; (l = i.data.indexOf(m, l + 1)) !== -1; ) a.push({ type: 7, index: s }), l += m.length - 1;
      }
      s++;
    }
  }
  static createElement(e, t) {
    const r = x.createElement("template");
    return r.innerHTML = e, r;
  }
}
function _(n, e, t = n, r) {
  var o, d;
  if (e === v) return e;
  let i = r !== void 0 ? (o = t._$Co) == null ? void 0 : o[r] : t._$Cl;
  const s = M(e) ? void 0 : e._$litDirective$;
  return (i == null ? void 0 : i.constructor) !== s && ((d = i == null ? void 0 : i._$AO) == null || d.call(i, !1), s === void 0 ? i = void 0 : (i = new s(n), i._$AT(n, t, r)), r !== void 0 ? (t._$Co ?? (t._$Co = []))[r] = i : t._$Cl = i), i !== void 0 && (e = _(n, i._$AS(n, e.values), i, r)), e;
}
class Ne {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: t }, parts: r } = this._$AD, i = ((e == null ? void 0 : e.creationScope) ?? x).importNode(t, !0);
    k.currentNode = i;
    let s = k.nextNode(), o = 0, d = 0, a = r[0];
    for (; a !== void 0; ) {
      if (o === a.index) {
        let c;
        a.type === 2 ? c = new R(s, s.nextSibling, this, e) : a.type === 1 ? c = new a.ctor(s, a.name, a.strings, this, e) : a.type === 6 && (c = new Ie(s, this, e)), this._$AV.push(c), a = r[++d];
      }
      o !== (a == null ? void 0 : a.index) && (s = k.nextNode(), o++);
    }
    return k.currentNode = x, i;
  }
  p(e) {
    let t = 0;
    for (const r of this._$AV) r !== void 0 && (r.strings !== void 0 ? (r._$AI(e, r, t), t += r.strings.length - 2) : r._$AI(e[t])), t++;
  }
}
class R {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, t, r, i) {
    this.type = 2, this._$AH = h, this._$AN = void 0, this._$AA = e, this._$AB = t, this._$AM = r, this.options = i, this._$Cv = (i == null ? void 0 : i.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const t = this._$AM;
    return t !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = t.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, t = this) {
    e = _(this, e, t), M(e) ? e === h || e == null || e === "" ? (this._$AH !== h && this._$AR(), this._$AH = h) : e !== this._$AH && e !== v && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Te(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== h && M(this._$AH) ? this._$AA.nextSibling.data = e : this.T(x.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var s;
    const { values: t, _$litType$: r } = e, i = typeof r == "number" ? this._$AC(e) : (r.el === void 0 && (r.el = P.createElement(we(r.h, r.h[0]), this.options)), r);
    if (((s = this._$AH) == null ? void 0 : s._$AD) === i) this._$AH.p(t);
    else {
      const o = new Ne(i, this), d = o.u(this.options);
      o.p(t), this.T(d), this._$AH = o;
    }
  }
  _$AC(e) {
    let t = ce.get(e.strings);
    return t === void 0 && ce.set(e.strings, t = new P(e)), t;
  }
  k(e) {
    ee(this._$AH) || (this._$AH = [], this._$AR());
    const t = this._$AH;
    let r, i = 0;
    for (const s of e) i === t.length ? t.push(r = new R(this.O(H()), this.O(H()), this, this.options)) : r = t[i], r._$AI(s), i++;
    i < t.length && (this._$AR(r && r._$AB.nextSibling, i), t.length = i);
  }
  _$AR(e = this._$AA.nextSibling, t) {
    var r;
    for ((r = this._$AP) == null ? void 0 : r.call(this, !1, !0, t); e && e !== this._$AB; ) {
      const i = e.nextSibling;
      e.remove(), e = i;
    }
  }
  setConnected(e) {
    var t;
    this._$AM === void 0 && (this._$Cv = e, (t = this._$AP) == null || t.call(this, e));
  }
}
class D {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, r, i, s) {
    this.type = 1, this._$AH = h, this._$AN = void 0, this.element = e, this.name = t, this._$AM = i, this.options = s, r.length > 2 || r[0] !== "" || r[1] !== "" ? (this._$AH = Array(r.length - 1).fill(new String()), this.strings = r) : this._$AH = h;
  }
  _$AI(e, t = this, r, i) {
    const s = this.strings;
    let o = !1;
    if (s === void 0) e = _(this, e, t, 0), o = !M(e) || e !== this._$AH && e !== v, o && (this._$AH = e);
    else {
      const d = e;
      let a, c;
      for (e = s[0], a = 0; a < s.length - 1; a++) c = _(this, d[r + a], t, a), c === v && (c = this._$AH[a]), o || (o = !M(c) || c !== this._$AH[a]), c === h ? e = h : e !== h && (e += (c ?? "") + s[a + 1]), this._$AH[a] = c;
    }
    o && !i && this.j(e);
  }
  j(e) {
    e === h ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class Le extends D {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === h ? void 0 : e;
  }
}
class De extends D {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== h);
  }
}
class je extends D {
  constructor(e, t, r, i, s) {
    super(e, t, r, i, s), this.type = 5;
  }
  _$AI(e, t = this) {
    if ((e = _(this, e, t, 0) ?? h) === v) return;
    const r = this._$AH, i = e === h && r !== h || e.capture !== r.capture || e.once !== r.once || e.passive !== r.passive, s = e !== h && (r === h || i);
    i && this.element.removeEventListener(this.name, this, r), s && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t;
    typeof this._$AH == "function" ? this._$AH.call(((t = this.options) == null ? void 0 : t.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class Ie {
  constructor(e, t, r) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = r;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    _(this, e);
  }
}
const V = C.litHtmlPolyfillSupport;
V == null || V(P, R), (C.litHtmlVersions ?? (C.litHtmlVersions = [])).push("3.3.0");
const Ge = (n, e, t) => {
  const r = (t == null ? void 0 : t.renderBefore) ?? e;
  let i = r._$litPart$;
  if (i === void 0) {
    const s = (t == null ? void 0 : t.renderBefore) ?? null;
    r._$litPart$ = i = new R(e.insertBefore(H(), s), s, void 0, t ?? {});
  }
  return i._$AI(n), i;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const w = globalThis;
let S = class extends $ {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t;
    const e = super.createRenderRoot();
    return (t = this.renderOptions).renderBefore ?? (t.renderBefore = e.firstChild), e;
  }
  update(e) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Ge(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return v;
  }
};
var ue;
S._$litElement$ = !0, S.finalized = !0, (ue = w.litElementHydrateSupport) == null || ue.call(w, { LitElement: S });
const W = w.litElementPolyfillSupport;
W == null || W({ LitElement: S });
(w.litElementVersions ?? (w.litElementVersions = [])).push("4.2.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const qe = { attribute: !0, type: String, converter: B, reflect: !1, hasChanged: Z }, Ve = (n = qe, e, t) => {
  const { kind: r, metadata: i } = t;
  let s = globalThis.litPropertyMetadata.get(i);
  if (s === void 0 && globalThis.litPropertyMetadata.set(i, s = /* @__PURE__ */ new Map()), r === "setter" && ((n = Object.create(n)).wrapped = !0), s.set(t.name, n), r === "accessor") {
    const { name: o } = t;
    return { set(d) {
      const a = e.get.call(this);
      e.set.call(this, d), this.requestUpdate(o, a, n);
    }, init(d) {
      return d !== void 0 && this.C(o, void 0, n, d), d;
    } };
  }
  if (r === "setter") {
    const { name: o } = t;
    return function(d) {
      const a = this[o];
      e.call(this, d), this.requestUpdate(o, a, n);
    };
  }
  throw Error("Unsupported decorator location: " + r);
};
function j(n) {
  return (e, t) => typeof t == "object" ? Ve(n, e, t) : ((r, i, s) => {
    const o = i.hasOwnProperty(s);
    return i.constructor.createProperty(s, r), o ? Object.getOwnPropertyDescriptor(i, s) : void 0;
  })(n, e, t);
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const We = (n) => n.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const xe = { CHILD: 2 }, ve = (n) => (...e) => ({ _$litDirective$: n, values: e });
class $e {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, t, r) {
    this._$Ct = e, this._$AM = t, this._$Ci = r;
  }
  _$AS(e, t) {
    return this.update(e, t);
  }
  update(e, t) {
    return this.render(...t);
  }
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const z = (n, e) => {
  var r;
  const t = n._$AN;
  if (t === void 0) return !1;
  for (const i of t) (r = i._$AO) == null || r.call(i, e, !1), z(i, e);
  return !0;
}, L = (n) => {
  let e, t;
  do {
    if ((e = n._$AM) === void 0) break;
    t = e._$AN, t.delete(n), n = e;
  } while ((t == null ? void 0 : t.size) === 0);
}, _e = (n) => {
  for (let e; e = n._$AM; n = e) {
    let t = e._$AN;
    if (t === void 0) e._$AN = t = /* @__PURE__ */ new Set();
    else if (t.has(n)) break;
    t.add(n), Ye(e);
  }
};
function Ke(n) {
  this._$AN !== void 0 ? (L(this), this._$AM = n, _e(this)) : this._$AM = n;
}
function Fe(n, e = !1, t = 0) {
  const r = this._$AH, i = this._$AN;
  if (i !== void 0 && i.size !== 0) if (e) if (Array.isArray(r)) for (let s = t; s < r.length; s++) z(r[s], !1), L(r[s]);
  else r != null && (z(r, !1), L(r));
  else z(this, n);
}
const Ye = (n) => {
  n.type == xe.CHILD && (n._$AP ?? (n._$AP = Fe), n._$AQ ?? (n._$AQ = Ke));
};
class Xe extends $e {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(e, t, r) {
    super._$AT(e, t, r), _e(this), this.isConnected = e._$AU;
  }
  _$AO(e, t = !0) {
    var r, i;
    e !== this.isConnected && (this.isConnected = e, e ? (r = this.reconnected) == null || r.call(this) : (i = this.disconnected) == null || i.call(this)), t && (z(this, e), L(this));
  }
  setValue(e) {
    if (We(this._$Ct)) this._$Ct._$AI(e, this);
    else {
      const t = [...this._$Ct._$AH];
      t[this._$Ci] = e, this._$Ct._$AI(t, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const K = () => new Je();
class Je {
}
const F = /* @__PURE__ */ new WeakMap(), Y = ve(class extends Xe {
  render(n) {
    return h;
  }
  update(n, [e]) {
    var r;
    const t = e !== this.G;
    return t && this.G !== void 0 && this.rt(void 0), (t || this.lt !== this.ct) && (this.G = e, this.ht = (r = n.options) == null ? void 0 : r.host, this.rt(this.ct = n.element)), h;
  }
  rt(n) {
    if (this.isConnected || (n = void 0), typeof this.G == "function") {
      const e = this.ht ?? globalThis;
      let t = F.get(e);
      t === void 0 && (t = /* @__PURE__ */ new WeakMap(), F.set(e, t)), t.get(this.G) !== void 0 && this.G.call(this.ht, void 0), t.set(this.G, n), n !== void 0 && this.G.call(this.ht, n);
    } else this.G.value = n;
  }
  get lt() {
    var n, e;
    return typeof this.G == "function" ? (n = F.get(this.ht ?? globalThis)) == null ? void 0 : n.get(this.G) : (e = this.G) == null ? void 0 : e.value;
  }
  disconnected() {
    this.lt === this.ct && this.rt(void 0);
  }
  reconnected() {
    this.rt(this.ct);
  }
});
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class X extends $e {
  constructor(e) {
    if (super(e), this.it = h, e.type !== xe.CHILD) throw Error(this.constructor.directiveName + "() can only be used in child bindings");
  }
  render(e) {
    if (e === h || e == null) return this._t = void 0, this.it = e;
    if (e === v) return e;
    if (typeof e != "string") throw Error(this.constructor.directiveName + "() called with a non-string value");
    if (e === this.it) return this._t;
    this.it = e;
    const t = [e];
    return t.raw = t, this._t = { _$litType$: this.constructor.resultType, strings: t, values: [] };
  }
}
X.directiveName = "unsafeHTML", X.resultType = 1;
const Qe = ve(X), Ze = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.3627 5.63727 5.63733 18.3627m12.72537.0L5.63733 5.63727" stroke="currentColor" stroke-width="1.25" stroke-miterlimit="10" stroke-linecap="square"/></svg>', et = 'html{font-size:6px}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:500;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Medium.woff2) format("woff2")}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:400;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Regular.woff2) format("woff2")}html,:root,:host{--bdk-alabaster: rgba(250,248,247,1);--bdk-ash: rgba(108,103,101,1);--bdk-beyond-bronze: rgba(157,83,39,1);--bdk-beyond-gold: rgba(158,91,5,1);--bdk-beyond-platin: rgba(132,93,104,1);--bdk-breuninger-orange: rgba(243,138,1,1);--bdk-breuninger-red: rgba(186,12,47,1);--bdk-bronze-background: rgba(248,236,232,1);--bdk-bronze-gradient: rgba(0,0,0,1);--bdk-coal-100: rgba(26,26,26,1);--bdk-coal-25: rgba(26,26,26,.25);--bdk-coal-50: rgba(26,26,26,.5);--bdk-coal-85: rgba(26,26,26,.85);--bdk-concrete: rgba(244,242,242,1);--bdk-error: rgba(178,12,22,1);--bdk-error-background: rgba(255,237,244,1);--bdk-eukalyptus: rgba(201,229,216,1);--bdk-gold-gradient: rgba(0,0,0,1);--bdk-home-and-living: rgba(186,180,180,1);--bdk-marketplace: rgba(16,53,185,1);--bdk-orange-background: rgba(255,244,235,1);--bdk-pebble: rgba(224,223,222,1);--bdk-platin-gradient: rgba(0,0,0,1);--bdk-private-luxury-sale: rgba(207,199,192,1);--bdk-red-hover-light: rgba(203,13,51,1);--bdk-sale: rgba(255,237,0,1);--bdk-silber-gradient: rgba(0,0,0,1);--bdk-smoke: rgba(146,141,139,1);--bdk-snow-100: rgba(255,255,255,1);--bdk-snow-25: rgba(255,255,255,.25);--bdk-snow-50: rgba(255,255,255,.5);--bdk-snow-85: rgba(255,255,255,.85);--bdk-success: rgba(19,98,51,1);--bdk-success-background: rgba(239,253,229,1);--bdk-space-0: 0rem;--bdk-space-0-5: .333333rem;--bdk-space-1: .666667rem;--bdk-space-10: 6.666667rem;--bdk-space-11: 7.333333rem;--bdk-space-12: 8rem;--bdk-space-13: 8.666667rem;--bdk-space-14: 9.333333rem;--bdk-space-15: 10rem;--bdk-space-16: 10.666667rem;--bdk-space-17: 11.333333rem;--bdk-space-18: 12rem;--bdk-space-19: 12.666667rem;--bdk-space-2: 1.333333rem;--bdk-space-2-5: 1.666667rem;--bdk-space-20: 13.333333rem;--bdk-space-21: 14rem;--bdk-space-22: 14.666667rem;--bdk-space-23: 15.333333rem;--bdk-space-24: 16rem;--bdk-space-25: 16.666666rem;--bdk-space-26: 17.333334rem;--bdk-space-27: 18rem;--bdk-space-28: 18.666666rem;--bdk-space-29: 19.333334rem;--bdk-space-3: 2rem;--bdk-space-30: 20rem;--bdk-space-31: 20.666666rem;--bdk-space-32: 21.333334rem;--bdk-space-33: 22rem;--bdk-space-34: 22.666666rem;--bdk-space-35: 23.333334rem;--bdk-space-4: 2.666667rem;--bdk-space-5: 3.333333rem;--bdk-space-6: 4rem;--bdk-space-7: 4.666667rem;--bdk-space-8: 5.333333rem;--bdk-space-9: 6rem;--bdk-radius-0: 0rem;--bdk-radius-1: .666667rem;--bdk-radius-1-5: 1rem;--bdk-radius-10: 6.666667rem;--bdk-radius-11: 7.333333rem;--bdk-radius-12: 8rem;--bdk-radius-2: 1.333333rem;--bdk-radius-3: 2rem;--bdk-radius-4: 2.666667rem;--bdk-radius-5: 3.333333rem;--bdk-radius-6: 4rem;--bdk-radius-7: 4.666667rem;--bdk-radius-8: 5.333333rem;--bdk-radius-9: 6rem;--bdk-radius-pill: 166.5rem;--bdk-button-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-button-l-font-size: 2.666667rem;--bdk-button-l-font-weight: 500;--bdk-button-l-letter-spacing: .05rem;--bdk-button-l-line-height: 4rem;--bdk-button-l-text-decoration: none;--bdk-button-l-text-transform: uppercase;--bdk-button-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-button-m-font-size: 2.333333rem;--bdk-button-m-font-weight: 500;--bdk-button-m-letter-spacing: .033333rem;--bdk-button-m-line-height: 3.666667rem;--bdk-button-m-text-decoration: none;--bdk-button-m-text-transform: uppercase;--bdk-button-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-button-s-font-size: 2rem;--bdk-button-s-font-weight: 500;--bdk-button-s-letter-spacing: .033333rem;--bdk-button-s-line-height: 3rem;--bdk-button-s-text-decoration: none;--bdk-button-s-text-transform: uppercase;--bdk-heading-2xl-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-2xl-font-size: 7rem;--bdk-heading-2xl-font-weight: 500;--bdk-heading-2xl-letter-spacing: .266667rem;--bdk-heading-2xl-line-height: 10.666667rem;--bdk-heading-2xl-text-decoration: none;--bdk-heading-2xl-text-transform: uppercase;--bdk-heading-2xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-2xs-font-size: 1.666667rem;--bdk-heading-2xs-font-weight: 500;--bdk-heading-2xs-letter-spacing: .016667rem;--bdk-heading-2xs-line-height: 2.666667rem;--bdk-heading-2xs-text-decoration: none;--bdk-heading-2xs-text-transform: uppercase;--bdk-heading-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-l-font-size: 4rem;--bdk-heading-l-font-weight: 500;--bdk-heading-l-letter-spacing: .116667rem;--bdk-heading-l-line-height: 6rem;--bdk-heading-l-text-decoration: none;--bdk-heading-l-text-transform: uppercase;--bdk-heading-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-m-font-size: 3.333333rem;--bdk-heading-m-font-weight: 500;--bdk-heading-m-letter-spacing: .083333rem;--bdk-heading-m-line-height: 5rem;--bdk-heading-m-text-decoration: none;--bdk-heading-m-text-transform: uppercase;--bdk-heading-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-s-font-size: 2.666667rem;--bdk-heading-s-font-weight: 500;--bdk-heading-s-letter-spacing: .05rem;--bdk-heading-s-line-height: 4rem;--bdk-heading-s-text-decoration: none;--bdk-heading-s-text-transform: uppercase;--bdk-heading-xl-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-xl-font-size: 5.333333rem;--bdk-heading-xl-font-weight: 500;--bdk-heading-xl-letter-spacing: .2rem;--bdk-heading-xl-line-height: 8rem;--bdk-heading-xl-text-decoration: none;--bdk-heading-xl-text-transform: uppercase;--bdk-heading-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-heading-xs-font-size: 2.333333rem;--bdk-heading-xs-font-weight: 500;--bdk-heading-xs-letter-spacing: .033333rem;--bdk-heading-xs-line-height: 3.666667rem;--bdk-heading-xs-text-decoration: none;--bdk-heading-xs-text-transform: uppercase;--bdk-list-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-list-m-font-size: 2.666667rem;--bdk-list-m-font-weight: 400;--bdk-list-m-letter-spacing: .066667rem;--bdk-list-m-line-height: 4rem;--bdk-list-m-text-decoration: none;--bdk-list-m-text-transform: inherit;--bdk-strong-2xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-2xs-font-size: 1.666667rem;--bdk-strong-2xs-font-weight: 500;--bdk-strong-2xs-letter-spacing: .016667rem;--bdk-strong-2xs-line-height: 2.666667rem;--bdk-strong-2xs-text-decoration: none;--bdk-strong-2xs-text-transform: inherit;--bdk-strong-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-l-font-size: 3.333333rem;--bdk-strong-l-font-weight: 500;--bdk-strong-l-letter-spacing: .1rem;--bdk-strong-l-line-height: 5rem;--bdk-strong-l-text-decoration: none;--bdk-strong-l-text-transform: inherit;--bdk-strong-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-m-font-size: 2.666667rem;--bdk-strong-m-font-weight: 500;--bdk-strong-m-letter-spacing: .066667rem;--bdk-strong-m-line-height: 4rem;--bdk-strong-m-text-decoration: none;--bdk-strong-m-text-transform: inherit;--bdk-strong-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-s-font-size: 2.333333rem;--bdk-strong-s-font-weight: 500;--bdk-strong-s-letter-spacing: .05rem;--bdk-strong-s-line-height: 3.666667rem;--bdk-strong-s-text-decoration: none;--bdk-strong-s-text-transform: inherit;--bdk-strong-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-strong-xs-font-size: 2rem;--bdk-strong-xs-font-weight: 500;--bdk-strong-xs-letter-spacing: .033333rem;--bdk-strong-xs-line-height: 3rem;--bdk-strong-xs-text-decoration: none;--bdk-strong-xs-text-transform: inherit;--bdk-text-l-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-l-font-size: 3.333333rem;--bdk-text-l-font-weight: 400;--bdk-text-l-letter-spacing: .1rem;--bdk-text-l-line-height: 5rem;--bdk-text-l-text-decoration: none;--bdk-text-l-text-transform: inherit;--bdk-text-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-m-font-size: 2.666667rem;--bdk-text-m-font-weight: 400;--bdk-text-m-letter-spacing: .066667rem;--bdk-text-m-line-height: 4rem;--bdk-text-m-text-decoration: none;--bdk-text-m-text-transform: inherit;--bdk-text-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-s-font-size: 2.333333rem;--bdk-text-s-font-weight: 400;--bdk-text-s-letter-spacing: .05rem;--bdk-text-s-line-height: 3.666667rem;--bdk-text-s-text-decoration: none;--bdk-text-s-text-transform: inherit;--bdk-text-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-text-xs-font-size: 2rem;--bdk-text-xs-font-weight: 400;--bdk-text-xs-letter-spacing: .033333rem;--bdk-text-xs-line-height: 3rem;--bdk-text-xs-text-decoration: none;--bdk-text-xs-text-transform: inherit;--bdk-underline-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-m-font-size: 2.666667rem;--bdk-underline-m-font-weight: 400;--bdk-underline-m-letter-spacing: .066667rem;--bdk-underline-m-line-height: 4rem;--bdk-underline-m-text-decoration: underline;--bdk-underline-m-text-transform: inherit;--bdk-underline-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-s-font-size: 2.333333rem;--bdk-underline-s-font-weight: 400;--bdk-underline-s-letter-spacing: .05rem;--bdk-underline-s-line-height: 3.666667rem;--bdk-underline-s-text-decoration: underline;--bdk-underline-s-text-transform: inherit;--bdk-underline-strong-m-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-strong-m-font-size: 2.666667rem;--bdk-underline-strong-m-font-weight: 500;--bdk-underline-strong-m-letter-spacing: .066667rem;--bdk-underline-strong-m-line-height: 4rem;--bdk-underline-strong-m-text-decoration: underline;--bdk-underline-strong-m-text-transform: inherit;--bdk-underline-strong-s-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-strong-s-font-size: 2.333333rem;--bdk-underline-strong-s-font-weight: 500;--bdk-underline-strong-s-letter-spacing: .05rem;--bdk-underline-strong-s-line-height: 3.666667rem;--bdk-underline-strong-s-text-decoration: underline;--bdk-underline-strong-s-text-transform: inherit;--bdk-underline-strong-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-strong-xs-font-size: 2rem;--bdk-underline-strong-xs-font-weight: 500;--bdk-underline-strong-xs-letter-spacing: .033333rem;--bdk-underline-strong-xs-line-height: 3rem;--bdk-underline-strong-xs-text-decoration: underline;--bdk-underline-strong-xs-text-transform: inherit;--bdk-underline-xs-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--bdk-underline-xs-font-size: 2rem;--bdk-underline-xs-font-weight: 400;--bdk-underline-xs-letter-spacing: .033333rem;--bdk-underline-xs-line-height: 3rem;--bdk-underline-xs-text-decoration: underline;--bdk-underline-xs-text-transform: inherit;--bdk-breakpoint-2xl: 1360px;--bdk-breakpoint-2xs: 320px;--bdk-breakpoint-3xl: 1660px;--bdk-breakpoint-app: 375px;--bdk-breakpoint-full-hd: 1920px;--bdk-breakpoint-l: 960px;--bdk-breakpoint-m: 760px;--bdk-breakpoint-s: 560px;--bdk-breakpoint-xl: 1160px;--bdk-breakpoint-xs: 360px;--bdk-elevation-above-header: 45;--bdk-elevation-above-menu: 55;--bdk-elevation-above-overlay: 85;--bdk-elevation-below-header: 35;--bdk-elevation-below-menu: 45;--bdk-elevation-below-overlay: 55;--bdk-elevation-header: 40;--bdk-elevation-layer: 90;--bdk-elevation-menu: 50;--bdk-elevation-offcanvas: 100;--bdk-elevation-overlay: 80;--bdk-shadow-elevation--1: 0rem -.833333rem .833333rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation--2: 0rem -1.666667rem 2.5rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation--3: 0rem -4.166667rem 5rem 0rem rgba(26,26,26,.04);--bdk-shadow-elevation--4: 0rem -6.666667rem 6.666667rem 0rem rgba(26,26,26,.04);--bdk-shadow-elevation-1: 0rem .833333rem .833333rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation-2: 0rem 1.666667rem 2.5rem 0rem rgba(26,26,26,.08);--bdk-shadow-elevation-3: 0rem 4.166667rem 5rem 0rem rgba(26,26,26,.04);--bdk-shadow-elevation-4: 0rem 6.666667rem 6.666667rem 0rem rgba(26,26,26,.04)}*,:before,:after{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }::backdrop{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }*,:before,:after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}:before,:after{--tw-content: ""}html,:host{line-height:1.5;-webkit-text-size-adjust:100%;-moz-tab-size:4;tab-size:4;font-family:Eduard Breuninger,ui-sans-serif,system-ui,sans-serif,"Apple Color Emoji","Segoe UI Emoji",Segoe UI Symbol,"Noto Color Emoji";font-feature-settings:normal;font-variation-settings:normal;-webkit-tap-highlight-color:transparent}body{margin:0;line-height:inherit}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;text-decoration:inherit}b,strong{font-weight:bolder}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-feature-settings:normal;font-variation-settings:normal;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-feature-settings:inherit;font-variation-settings:inherit;font-size:100%;font-weight:inherit;line-height:inherit;letter-spacing:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,input:where([type=button]),input:where([type=reset]),input:where([type=submit]){-webkit-appearance:button;background-color:transparent;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{list-style:none;margin:0;padding:0}dialog{padding:0}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:#9ca3af}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}[hidden]:where(:not([hidden=until-found])){display:none}.bdk-tw-heading-m{font-family:Eduard Breuninger;font-size:3.333333rem;font-weight:500;line-height:5rem;letter-spacing:.083333rem;text-transform:uppercase}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0}.static{position:static}.fixed{position:fixed}.relative{position:relative}.inset-0{top:0rem;right:0rem;bottom:0rem;left:0rem}.z-10{z-index:10}.z-20{z-index:20}.mb-2{margin-bottom:1.333333rem}.ml-auto{margin-left:auto}.block{display:block}.inline{display:inline}.flex{display:flex}.h-\\[800px\\]{height:800px}.h-full{height:100%}.w-full{width:100%}.transform{transform:translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))}.resize{resize:both}.items-center{align-items:center}.justify-center{justify-content:center}.overflow-hidden{overflow:hidden}.bg-black{--tw-bg-opacity: 1;background-color:rgb(0 0 0 / var(--tw-bg-opacity, 1))}.bg-opacity-50{--tw-bg-opacity: .5}.shadow{--tw-shadow: 0 1px 3px 0 rgb(0 0 0 / .1), 0 1px 2px -1px rgb(0 0 0 / .1);--tw-shadow-colored: 0 1px 3px 0 var(--tw-shadow-color), 0 1px 2px -1px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.filter{filter:var(--tw-blur) var(--tw-brightness) var(--tw-contrast) var(--tw-grayscale) var(--tw-hue-rotate) var(--tw-invert) var(--tw-saturate) var(--tw-sepia) var(--tw-drop-shadow)}.ease-in{transition-timing-function:cubic-bezier(.4,0,1,1)}.ease-out{transition-timing-function:cubic-bezier(0,0,.2,1)}@keyframes backdrop-fade{0%{background:transparent}to{background:color-mix(in srgb,var(--bdk-coal-100) 50%,transparent)}}@keyframes backdrop-hide{0%{background:color-mix(in srgb,var(--bdk-coal-100) 50%,transparent)}to{background:transparent}}dialog{--bdk-dialog-min-height: 240px;margin:0;padding:0;inset:unset;max-width:unset}dialog::backdrop{position:fixed;top:0;right:0;bottom:0;left:0}dialog[open]::backdrop{animation:backdrop-fade var(--bdk-transition-duration-default) ease-in forwards}dialog.hide::backdrop{animation:backdrop-hide var(--bdk-transition-duration-default) ease-in forwards}.drawer{display:flex;flex-direction:column;background-color:var(--bdk-snow-100)}.drawer:not([open]){display:none}@media all and (max-width: 760px){.drawer{left:0;bottom:0;width:100%;max-height:calc(100dvh - var(--bdk-space-10));height:auto;border-radius:var(--bdk-space-3) var(--bdk-space-3) 0 0;min-height:var(--bdk-dialog-min-height);padding-inline:var(--bdk-space-5)}:host([full-screen]) .drawer{height:100dvh;max-height:100dvh;border-radius:0}}@media all and (min-width: 760px){.drawer{top:0;right:0;height:100dvh;max-height:100dvh;width:33.3vw;padding-inline:var(--bdk-space-10)}}@media all and (min-width: 760px) and (max-width: 1160px){.drawer{min-width:360px}}@media all and (min-width: 1920px){.drawer{max-width:556px}}.drawer-header{width:100%}@media all and (max-width: 760px){.drawer-header{padding-top:var(--bdk-space-4);padding-bottom:var(--bdk-space-2)}}@media all and (min-width: 760px){.drawer-header{padding-top:var(--bdk-space-6);padding-bottom:var(--bdk-space-8)}}.drawer-header-bar{display:grid;grid-template-columns:min-content auto min-content;gap:var(--bdk-space-4);align-items:center;margin-bottom:var(--bdk-space-4)}.drawer-content{flex:1;overflow-y:auto;padding-bottom:var(--bdk-space-10)}.drawer-footer{isolation:isolate;padding-top:var(--bdk-space-4);padding-bottom:var(--bdk-space-9)}.drawer-button{width:var(--bdk-space-6);aspect-ratio:1}.drawer-button:focus-visible{outline:2px solid var(--bdk-focus-color);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color)}.drawer-button:hover,.drawer-button:active{color:var(--bdk-red-hover-light)}@-moz-document url-prefix(){dialog::backdrop{background:color-mix(in srgb,var(--bdk-coal-100) 50%,transparent)}}@media (prefers-reduced-motion: reduce){dialog.hide::backdrop,dialog[open]::backdrop{animation:none 0s;background:color-mix(in srgb,var(--bdk-coal-100) 50%,transparent)}}';
function ye(n, e = !0) {
  return Array.isArray(n) ? n.map((t) => {
    if (e && Array.isArray(t))
      throw new Error(
        "The tuple syntax is not supported for `screens`."
      );
    if (typeof t == "string")
      return {
        name: t.toString(),
        not: !1,
        values: [{ min: t, max: void 0 }]
      };
    let [r, i] = t;
    return r = r.toString(), typeof i == "string" ? {
      name: r,
      not: !1,
      values: [{ min: i, max: void 0 }]
    } : Array.isArray(i) ? {
      name: r,
      not: !1,
      values: i.map((s) => pe(s))
    } : { name: r, not: !1, values: [pe(i)] };
  }) : ye(Object.entries(n ?? {}), !1);
}
function pe({
  "min-width": n,
  min: e = n,
  max: t,
  raw: r
} = {}) {
  return { min: e, max: t, raw: r };
}
const tt = { "2xs_and_up": "320px", "2xs_to_xs": { min: "320px", max: "359px" }, xs_and_up: "360px", xs_to_app: { min: "360px", max: "374px" }, app_and_up: "375px", app_to_s: { min: "375px", max: "559px" }, s_and_up: "560px", s_to_m: { min: "560px", max: "759px" }, m_and_up: "760px", m_to_l: { min: "760px", max: "959px" }, l_and_up: "960px", l_to_xl: { min: "960px", max: "1159px" }, xl_and_up: "1160px", xl_to_2xl: { min: "1160px", max: "1359px" }, "2xl_and_up": "1360px", "2xl_to_3xl": { min: "1360px", max: "1659px" }, "3xl_and_up": "1660px", "3xl_to_full-hd": { min: "1660px", max: "1919px" }, "full-hd_and_up": "1920px", "up_to_full-hd": { max: "1919px" }, up_to_3xl: { max: "1659px" }, up_to_2xl: { max: "1359px" }, up_to_xl: { max: "1159px" }, up_to_l: { max: "959px" }, up_to_m: { max: "759px" }, up_to_s: { max: "559px" }, up_to_app: { max: "374px" }, up_to_xs: { max: "359px" }, up_to_2xs: { max: "319px" } }, rt = {
  screens: tt
};
function it(n) {
  return n = Array.isArray(n) ? n : [n], n.map((e) => {
    let t = e.values.map((r) => r.raw !== void 0 ? r.raw : [
      r.min && `min-width: ${r.min}`,
      r.max && `max-width: ${r.max}`
    ].filter(Boolean).join(" and "));
    return e.not ? `not all and ${t}` : t;
  }).join(", ");
}
function nt() {
  const n = ye(rt.screens), e = "m_and_up", t = n.find((r) => r.name === e);
  return t || console.log(`No \`${e}\` screen found.`), it(t);
}
const st = (n, e) => n.querySelector(`[slot=${e}]`);
var ot = Object.defineProperty, I = (n, e, t, r) => {
  for (var i = void 0, s = n.length - 1, o; s >= 0; s--)
    (o = n[s]) && (i = o(e, t, i) || i);
  return i && ot(e, t, i), i;
};
const at = "drawer:open", dt = "drawer:close", te = class te extends S {
  constructor() {
    super(...arguments), this.drawerRef = K(), this.drawerHeaderRef = K(), this.drawerFooterRef = K(), this.id = "", this.open = !1, this.fullScreen = !1, this.closeAriaLabel = "Close", this.handleEscapeKey = (e) => {
      e.key === "Escape" && (e.preventDefault(), this.hide());
    };
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), this.handleOutsideClick = this.handleOutsideClick.bind(this), this.createAnimations = this.createAnimations.bind(this), window.addEventListener("resize", this.createAnimations), (e = this.drawerRef.value) == null || e.addEventListener(
      "animationend",
      (t) => {
        console.log("animated", t.target);
      }
    ), this.onClose = this.onClose.bind(this);
  }
  disconnectedCallback() {
    var e, t, r, i;
    document.body.inert = !1, window.removeEventListener("resize", this.createAnimations), (e = this.drawerRef.value) == null || e.removeEventListener(
      "click",
      this.handleOutsideClick
    ), (t = this.drawerRef.value) == null || t.removeEventListener("close", this.onClose), (r = this.showDrawerAnimation) == null || r.cancel(), (i = this.hideDrawerAnimation) == null || i.cancel(), super.disconnectedCallback();
  }
  attributeChangedCallback(e, t, r) {
    super.attributeChangedCallback(e, t, r), e === "open" && (r !== null ? this.show() : this.hide());
  }
  firstUpdated() {
    this.drawerRef.value && (this.drawerRef.value.addEventListener("close", this.onClose), this.createAnimations());
  }
  createAnimations() {
    const e = this.drawerRef.value;
    if (!e)
      return;
    const r = window.matchMedia(`(${nt()})`).matches ? "translateX" : "translateY", i = new KeyframeEffect(
      e,
      [
        { transform: `${r}(100%)` },
        { transform: `${r}(0)` }
      ],
      { duration: 300, easing: "ease-out" }
    );
    this.showDrawerAnimation = new Animation(
      i,
      document.timeline
    );
    const s = new KeyframeEffect(
      e,
      [
        { transform: `${r}(0)` },
        { transform: `${r}(100%)` }
      ],
      { duration: 200, easing: "ease-in" }
    );
    this.hideDrawerAnimation = new Animation(
      s,
      document.timeline
    );
  }
  get eventPrefix() {
    return this.id ? `${this.id}-` : "";
  }
  dispatchOpenEvent() {
    const e = new CustomEvent(
      `${this.eventPrefix}${at}`,
      {
        bubbles: !0
      }
    );
    this.dispatchEvent(e);
  }
  dispatchCloseEvent() {
    const e = new CustomEvent(
      `${this.eventPrefix}${dt}`,
      {
        bubbles: !0,
        composed: !0
      }
    );
    this.dispatchEvent(e);
  }
  handleOutsideClick(e) {
    const t = e.target;
    if (t.tagName !== "DIALOG")
      return;
    const r = t.getBoundingClientRect();
    r.top <= e.clientY && e.clientY <= r.top + r.height && r.left <= e.clientX && e.clientX <= r.left + r.width || this.hide();
  }
  show() {
    var e, t, r;
    document.body.inert = !0, this.open = !0, (e = this.drawerRef.value) == null || e.showModal(), (t = this.drawerRef.value) == null || t.addEventListener("click", this.handleOutsideClick), document.addEventListener("keydown", this.handleEscapeKey), this.prefersReducedMotion() || (r = this.showDrawerAnimation) == null || r.play(), this.dispatchOpenEvent();
  }
  async hide() {
    var e, t, r, i;
    this.prefersReducedMotion() || ((e = this.drawerRef.value) == null || e.classList.add("hide"), (t = this.hideDrawerAnimation) == null || t.play(), await ((r = this.hideDrawerAnimation) == null ? void 0 : r.finished), (i = this.drawerRef.value) == null || i.classList.remove("hide")), document.body.inert = !1, document.removeEventListener("keydown", this.handleEscapeKey), this.onClose();
  }
  /**
   * @description
   * Closes all open drawers on the page.
   * Note: Does not close nested drawers in shadow roots.
   */
  closeAll() {
    document.querySelectorAll(
      "bdk-drawer[open]"
    ).forEach((t) => {
      try {
        t.hide();
      } catch (r) {
        console.error("Failed to hide bdk-drawer:", t, r);
      }
    });
  }
  onClose() {
    var e, t;
    this.open = !1, (e = this.drawerRef.value) == null || e.close(), (t = this.drawerRef.value) == null || t.removeEventListener(
      "click",
      this.handleOutsideClick
    ), this.dispatchCloseEvent();
  }
  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  render() {
    return T`<dialog id="drawer" class="drawer" ${Y(this.drawerRef)} aria-labelledby="drawer-title" aria-describedby="drawer-description" aria-modal="true" data-testid="${this.id}-drawer">${this.headerHTML()} ${this.bodyHTML()} ${this.footerHTML()}</dialog>`;
  }
  headerHTML() {
    return T`<div id="drawer-header" ${Y(this.drawerHeaderRef)} class="drawer-header"><div class="drawer-header-bar"><div><slot name="header-start"></slot></div><div><slot name="header-center"></slot></div><button class="drawer-button ml-auto bdk-tw-focus" @click="${this.hide}" type="button" data-testid="bdk-drawer-close-button" aria-label="${this.closeAriaLabel}"><span role="presentation">${Qe(Ze)}</span></button></div><div class="bdk-tw-heading-m text-coal" id="drawer-title"><slot name="title"></slot></div><div id="drawer-description" class="sr-only"><slot name="description"></slot></div></div>`;
  }
  bodyHTML() {
    return T`<div id="drawer-content" class="drawer-content"><slot></slot></div>`;
  }
  footerHTML() {
    if (st(this, "footer"))
      return T`<div class="drawer-footer" ${Y(this.drawerFooterRef)}><slot name="footer"></slot></div>`;
  }
};
te.styles = Ae`${me(et)}`;
let y = te;
I([
  j({ type: String })
], y.prototype, "id");
I([
  j({ type: Boolean, reflect: !0 })
], y.prototype, "open");
I([
  j({ type: Boolean, reflect: !0, attribute: "full-screen" })
], y.prototype, "fullScreen");
I([
  j({ type: String, reflect: !0, attribute: "close-aria-label" })
], y.prototype, "closeAriaLabel");
export {
  y as Drawer
};
